/** Legacy queue routing. Clinical chart writes use explicit typed adapters or
 * retained recovery drafts; sensitive finalization requires server confirmation. */

export const OFFLINE_CONFIG = {
  // Endpoints that should NEVER be cached (GET) or queued (writes)
  exclude: {
    // Patterns that exclude both caching and queuing
    patterns: [
      /^\/api\/auth\//,  // All auth endpoints (login, register, 2FA, etc.)
    ],
    
    // Specific endpoints that shouldn't be cached (but can be queued for writes)
    noCache: [
      /^\/api\/admin\/stats$/,  // Real-time stats
      /^\/api\/revenue-cycle(?:\/|$)/, // Billing evidence and permissions require the active server context.
      /^\/api\/pregnancies\/[^/]+\/contraction-timer(?:\/|$)/,
    ],
    
    // Specific endpoints that shouldn't be queued (but can be cached for GET)
    noQueue: [
      // Opening a demographics editor is session setup, not a clinical change.
      /^\/api\/clients\/[^/]+\/demographics\/sessions$/,
      /^\/api\/organization\/custom-visit-templates(?:\/|$)/,
      /^\/api\/clients\/[^/]+\/custom-visits\/[^/]+\/(lock|void|addendum)$/,
      // Checkout, review, and handoff must never report success before the server confirms them.
      /^\/api\/revenue-cycle(?:\/|$)/,
      // The timer must only acknowledge server-confirmed saves and deletions.
      /^\/api\/pregnancies\/[^/]+\/contraction-timer(?:\/|$)/,
      /^\/api\/clients\/[^/]+\/profile-image$/,  // Client chart photo uploads (FormData can't be easily queued)
      /^\/api\/user\/[^/]+\/profile-image$/,  // Legacy user photo uploads (FormData can't be easily queued)
      /^\/api\/sandbox\/convert-to-live$/,  // Account/org transitions must happen immediately
      /^\/api\/messageHub(?:\/|$)/i,  // SignalR negotiation/transport calls are ephemeral
      // Device registration is reconciled by the push manager using the current
      // browser subscription, permission, and account. Old queued registrations
      // must not reactivate an endpoint after sign-out or an explicit opt-out.
      /^\/api\/push\/(?:subscribe|unsubscribe)\/?$/,
      // Consult-note editors display a server-confirmed "Saved" state and
      // already retain failed drafts for an explicit retry. A synthetic 202
      // from the legacy queue would otherwise look like a successful save.
      /^\/api\/appointments\/[^/]+\/notes$/,
      // Intake draft saves require a fresh expectedSaveVersion from GET /intakes/current.
      /^\/api\/clients\/[^/]+\/intakes\/[^/]+\/(draft|complete)$/,
      // Pending intake accept/dismiss requires server HMAC configuration (vela-api).
      /^\/api\/clients\/[^/]+\/medications\/pending-intake(?:\/|$)/,
    ],
  },
  
  // Cache settings
  cache: {
    defaultMaxAge: 60 * 60 * 24,  // 24 hours
    defaultMaxEntries: 500,
  }
};

// Clinical writes need stronger guarantees than the legacy best-effort HTTP
// queue. They are persisted as recoverable chart drafts and never receive a
// synthetic success response.
export const CLINICAL_CHART_WRITE_PATTERNS = [
  /^\/api\/pregnancies\/[^/]+\/(prenatal|postpartum|newborn|lactation)-visits(?:\/[^/]+)?$/,
  /^\/api\/clients\/[^/]+\/custom-visits(?:\/[^/]+)?$/,
  /^\/api\/clients\/[^/]+\/well-woman-visits(?:\/[^/]+)?$/,
];

export function isClinicalChartWrite(pathname, method) {
  return ['POST', 'PUT', 'PATCH'].includes(method)
    && CLINICAL_CHART_WRITE_PATTERNS.some((pattern) => pattern.test(pathname));
}

// These editors do not yet have the visit protocol's replay guarantees. Keep
// failed writes for review, but never pretend an unconfirmed create has an ID
// or automatically replay a stale episode snapshot.
export function isClinicalRecoveryWrite(pathname, method) {
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return false;
  if (/\/(lock|unlock|lock-birth-record|void|addendum|discharge)$/.test(pathname)) return false;
  return [
    ...(method === 'DELETE' ? CLINICAL_CHART_WRITE_PATTERNS : []),
    /^\/api\/pregnancies\/[^/]+\/(labor-episodes|babies|birth-events)(?:\/[^/]+)?$/,
    /^\/api\/clients\/[^/]+\/babies(?:\/[^/]+)?$/,
    /^\/api\/babies\/[^/]+\/birth-events(?:\/[^/]+(?:\/(postpartum-assessment|resuscitation|hemorrhage)(?:\/(interventions|events)(?:\/[^/]+)?)?)?)?$/,
    /^\/api\/birth-events\/[^/]+\/labor-records(?:\/[^/]+)?$/,
    /^\/api\/fht-sessions\/complete$/,
    /^\/api\/clients\/[^/]+\/medications(?:\/[^/]+(?:\/discontinue)?)?$/,
    /^\/api\/(?:babies\/[^/]+\/)?ballard(?:-assessments|-scale)?(?:\/[^/]+)*$/,
  ].some(pattern => pattern.test(pathname));
}

// Requests written by the previous best-effort queue can still contain
// clinical chart changes. Those writes must stay available for explicit
// recovery because they predate the chart draft idempotency protections.
// Everything else can be retried automatically after connectivity returns.
export function shouldAutoReplayQueuedRequest(item, origin = window.location.origin) {
  if (!item?.url || !item?.method) return false;
  try {
    const pathname = new URL(item?.url, origin).pathname;
    const method = String(item?.method || '').toUpperCase();
    return !shouldDiscardQueuedRequest(item, origin)
      && !isClinicalChartWrite(pathname, method)
      && !isClinicalRecoveryWrite(pathname, method)
      && !requiresConfirmedWrite(pathname, method);
  } catch {
    return false;
  }
}

// Real-time transport requests (for example SignalR negotiate/poll calls) are
// only valid for the page/session that created them. Replaying one later can
// never restore the original connection and may start a reconnect loop.
export function shouldDiscardQueuedRequest(item, origin = window.location.origin) {
  if (!item?.url || !item?.method) return false;
  try {
    const pathname = new URL(item.url, origin).pathname;
    // Preserve writes queued by older timer versions. New timer writes bypass
    // the queue, but an update must not discard previously recorded contractions.
    if (requiresConfirmedWrite(pathname, String(item.method).toUpperCase())) return false;
    if (/^\/api\/pregnancies\/[^/]+\/contraction-timer(?:\/|$)/.test(pathname)) return false;
    return OFFLINE_CONFIG.exclude.noQueue.some((pattern) => pattern.test(pathname));
  } catch {
    return false;
  }
}

/**
 * Check if an endpoint should be excluded from offline handling
 * @param {string} pathname - The URL pathname (e.g., "/api/clients/123")
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {Object} config - Configuration object (defaults to OFFLINE_CONFIG)
 * @returns {boolean} - True if endpoint should be excluded
 */
export function shouldExclude(pathname, method, config = OFFLINE_CONFIG) {
  // Check general exclusion patterns (apply to all methods)
  for (const pattern of config.exclude.patterns) {
    if (pattern.test(pathname)) return true;
  }
  
  // Method-specific exclusions
  if (method === 'GET' && config.exclude.noCache) {
    for (const pattern of config.exclude.noCache) {
      if (pattern.test(pathname)) return true;
    }
  }
  
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && config.exclude.noQueue) {
    for (const pattern of config.exclude.noQueue) {
      if (pattern.test(pathname)) return true;
    }
  }
  
  return false;
}

/**
 * Check if a request contains FormData (file uploads)
 * FormData requests shouldn't be queued as they're large and can't be easily serialized
 * @param {Object} init - Fetch init options
 * @returns {boolean} - True if request contains FormData
 */
export function isFormDataRequest(init) {
  if (!init || !init.body) return false;
  return init.body instanceof FormData;
}

/**
 * Check if a URL is an API endpoint
 * @param {string} url - Full URL or pathname
 * @param {string} origin - Optional origin for relative URLs
 * @returns {boolean} - True if URL is an API endpoint
 */
export function isApiEndpoint(url, origin = window.location.origin) {
  try {
    const urlObj = typeof url === 'string' && url.startsWith('http') 
      ? new URL(url)
      : new URL(url, origin);
    return urlObj.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

// These workflows need server-created identifiers and cannot safely replay an
// ambiguous write. Keep this separate from noQueue: legacy queued records must
// not be deleted merely because new writes now require confirmation.
export function requiresConfirmedWrite(pathname, method) {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && (
    /^\/api\/clients\/[^/]+\/intakes\/[^/]+\/update$/.test(pathname) ||
    /^\/api\/clients\/[^/]+\/chart-mutations$/.test(pathname) ||
    /^\/api\/pregnancies\/[^/]+\/contraction-timer(?:\/|$)/.test(pathname) ||
    /\/(lock|unlock|lock-birth-record|void|addendum|discharge)$/.test(pathname) ||
    /^\/api\/appointments(?:\/[^/]+(?:\/cancel)?)?$/.test(pathname) ||
    /^\/api\/appointment-day-groups(?:\/[^/]+)?$/.test(pathname) ||
    /^\/api\/pregnancies\/[^/]+\/transfers\/labor-transfer$/.test(pathname) ||
    /^\/api\/transfers\/labor-transfer\/[^/]+$/.test(pathname)
  );
}
